import { useState, useEffect, useCallback } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import { nanoid } from '@reduxjs/toolkit';
import Zoom from '@mui/material/Zoom';
import cn from 'classnames';

import { ListTable, LTHeader, LTItem, LTRow, LTMobileItem } from 'components/common/ListTable';
import IconBtn from 'components/common/IconBtn';
import CustomizedTooltip from 'components/common/CustomizedTooltip/CustomizedTooltip';
import { ReactComponent as BasketIcon } from 'assets/icons/basket.svg';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg';
import { ReactComponent as PlusIcon } from 'assets/icons/plus-simple.svg';
import { MilestoneApp, MilestonePayload } from 'api';
import StyledTableInputWithError, { StyledTableInput } from './TableInput.styled';
import styles from './MilestonesAndFees.module.scss';
import DOMAIN from '../../../../../../../assets/domains.json';
import { DomainKey } from 'data/constants/domainApiKey';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { SvgIcon } from '@mui/material';
import { BiTrash } from 'react-icons/bi';
import { useWindowSize } from 'hooks/useWindowSize';

type MilestoneAndFee = Pick<MilestoneApp, 'name' | 'description' | 'price'> & { id: string };

type Props = {
    milestones: MilestoneApp[];
    editMode: boolean;
    isCreating: boolean;
    onCurrentMilestonesList: (payload: MilestonePayload[]) => void;
    onHasErrors?: (value: boolean) => void;
};

const OldMilestonesAndFees = ({
    milestones,
    editMode,
    isCreating,
    onCurrentMilestonesList,
    onHasErrors,
}: Props) => {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();
    const [preparedMilestones, setPreparedMilestones] = useState<MilestoneAndFee[]>([]);
    const [name, setName] = useState<string>('');
    const [explanation, setExplanation] = useState<string>('');
    const [price, setPrice] = useState<string>('');

    const [milestoneIdInEditMode, setMilestoneIdInEditMode] = useState<string>('');
    const [hasErrors, setErrors] = useState<boolean>(false);

    const [openMilNameTooltip, setOpenMilNameTooltip] = useState<string>('');
    const [openMilExplanationTooltip, setOpenMilExplanationTooltip] = useState<string>('');

    const [width, height] = useWindowSize();
    const [mobile, setMobile] = useState(false);

    useEffect(() => {
        if (width >= 769) {
            setMobile(false);
        }
        if (width < 769) {
            setMobile(true);
        }
    }, [width, height]);

    const oldGridStyle: React.CSSProperties = {
        display: 'grid',
        gridTemplateColumns: editMode
            ? 'minmax(200px, 3fr) minmax(200px, 4fr) 120px 90px'
            : 'minmax(200px, 3fr) minmax(200px, 4fr) 120px',
        alignItems: 'center',
        width: '100%',
    };

    const cellLeft: React.CSSProperties = {
        width: '100%',
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        textAlign: 'left',
    };

    const cellCenter: React.CSSProperties = {
        width: '100%',
        minWidth: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
    };

    useEffect(() => {
        onCurrentMilestonesList(preparedMilestones);
    }, [preparedMilestones, onCurrentMilestonesList]);

    useEffect(() => {
        convertData(milestones);
    }, [milestones, editMode]);

    const convertData = (arr: MilestoneApp[]) => {
        const converted = arr.map<MilestoneAndFee>((milestone) => ({
            id: milestone.id.toString(),
            name: milestone.name,
            description: milestone.description,
            price: milestone.price,
        }));

        setPreparedMilestones(converted);
    };

    const addMilestone = () => {
        const newMilestone: MilestoneAndFee = {
            id: nanoid(),
            name: name,
            description: explanation,
            price: price,
        };
        setPreparedMilestones((prevState) => [...prevState, newMilestone]);
        resetInputs();
    };

    const removeMilestone = (id: string) => {
        const filteredArr = preparedMilestones.filter((milestone) => milestone.id !== id);
        setPreparedMilestones(filteredArr);
    };

    const handlePriceInput = (value: string) => {
        if (!isNaN(Number(value))) {
            setPrice(value);
        }
    };

    const resetInputs = () => {
        setName('');
        setExplanation('');
        setPrice('');
    };

    const getNamePlaceholder = (rowNumber: number) => {
        const commonString = t('milestoneName', { lng: language });

        switch (rowNumber) {
            case 0:
                return `${language === 'EN' ? 'First' : "D'abord"} ${commonString}`;
            case 1:
                return `${language === 'EN' ? 'Second' : 'Seconde'} ${commonString}`;
            case 2:
                return `${language === 'EN' ? 'Third' : 'Troisième'} ${commonString}`;
            case 3:
                return `${language === 'EN' ? 'Fourth' : 'Quatrième'} ${commonString}`;
            case 4:
                return `${language === 'EN' ? 'Fifth' : 'Cinquième'} ${commonString}`;
            case 5:
                return `${language === 'EN' ? 'Sixth' : 'Sixième'} ${commonString}`;
        }
    };

    const toggleRowInEditMode = (milId: string) => {
        if (milestoneIdInEditMode === milId) {
            setMilestoneIdInEditMode('');
            return;
        }
        setMilestoneIdInEditMode(milId);
    };

    const changeMilestone = (
        fieldName: 'name' | 'description' | 'price',
        value: string,
        milId: string
    ) => {
        if (fieldName === 'price' && isNaN(Number(value))) return;

        setPreparedMilestones((prevState) => {
            const targetMilestone = preparedMilestones.find((mil) => mil.id === milId);
            if (targetMilestone) targetMilestone[fieldName] = value;
            return [...prevState];
        });
    };

    const getTotalPrice = useCallback(() => {
        if (preparedMilestones) {
            const prices = preparedMilestones.map((mil) => Number(mil.price));
            const totalFee = prices.reduce<number>((acc, curr) => {
                const tmp = acc + curr;
                return tmp;
            }, 0);
            return totalFee;
        }
        return 0;
    }, [preparedMilestones]);

    const isAvailableFeeForFirstMilestone = useCallback(() => {
        if (DomainKey === DOMAIN.ALIRO) {
            const totalPrice = getTotalPrice();
            const allowedPriceForFirstFee = 20;
            const percentageOfTotalFirstMil =
                (Number(preparedMilestones[0]?.price) * 100) / Number(totalPrice);
            if (percentageOfTotalFirstMil < allowedPriceForFirstFee) {
                return false;
            }
            return true;
        } else {
            return true;
        }
    }, [getTotalPrice, preparedMilestones]);

    const getFeeStyles = (milIndex: number) => {
        if (milIndex === 0) {
            return cn({ [styles.danger]: !isAvailableFeeForFirstMilestone() });
        }
    };

    const validateMaxLength = (attribute: 'name' | 'explanation', value: string): string => {
        if (attribute === 'name' && value.length > 64) {
            return 'Max 64 characters allowed';
        }
        if (attribute === 'explanation' && value.length > 128) {
            return 'Max 128 characters allowed';
        }
        return '';
    };

    const validateInputLength = (attribute: 'name' | 'explanation', value: string): string => {
        if (value.length === 0) return 'Required field';

        return validateMaxLength(attribute, value);
    };

    const hasMilestonesErrors = useCallback((): boolean => {
        return preparedMilestones.some((mil) => {
            return (
                mil.name.length === 0 ||
                mil.description.length === 0 ||
                mil.price.length === 0 ||
                mil.name.length > 64 ||
                mil.description.length > 128
            );
        });
    }, [preparedMilestones]);

    useEffect(() => {
        const hasArrayErrors: boolean = hasMilestonesErrors();
        if (onHasErrors) {
            onHasErrors(hasArrayErrors || !isAvailableFeeForFirstMilestone());
        }
        setErrors(hasArrayErrors);
    }, [preparedMilestones, hasMilestonesErrors, isAvailableFeeForFirstMilestone, onHasErrors]);

    // Tooltips
    const handleTooltipClose = (attribute: 'name' | 'explanation') => {
        setTimeout(() => {
            if (attribute === 'explanation') {
                setOpenMilExplanationTooltip('');
                return;
            }
            setOpenMilNameTooltip('');
        }, 270);
    };

    const handleTooltipOpen = (attribute: 'name' | 'explanation', milId: string) => {
        setTimeout(() => {
            if (attribute === 'explanation') {
                setOpenMilExplanationTooltip(milId);
                return;
            }
            setOpenMilNameTooltip(milId);
        }, 260);
    };

    const isCreateBtnEnable =
        name &&
        explanation &&
        price &&
        !validateMaxLength('name', name) &&
        !validateMaxLength('explanation', explanation);

    return (
        <div className={styles['table-wrapper']}>
            <ListTable style={{ position: 'relative' }}>
                <LTHeader innerStyle={mobile ? undefined : oldGridStyle}>
                    <LTItem style={cellLeft}>{t('milestone', { lng: language })}</LTItem>
                    <LTItem style={cellLeft}>{t('explanation', { lng: language })}</LTItem>
                    <LTItem style={cellLeft}>{t('fee', { lng: language })}</LTItem>
                    {editMode && (
                        <LTItem style={cellCenter}>{t('actions', { lng: language })}</LTItem>
                    )}
                </LTHeader>
                {preparedMilestones.map((milestone, idx, arr) => (
                    <LTRow
                        key={milestone.id}
                        style={{ position: 'relative' }}
                        innerStyle={mobile ? undefined : oldGridStyle}
                    >
                        {/*FULL SCREEN VERSION*/}
                        <LTItem style={{ ...cellLeft, paddingRight: '8px', overflow: 'visible' }}>
                            {milestoneIdInEditMode === milestone.id ? (
                                <StyledTableInputWithError
                                    value={milestone.name}
                                    onChange={(event) =>
                                        changeMilestone('name', event.target.value, milestone.id)
                                    }
                                    placeholder={getNamePlaceholder(idx)}
                                    errorMsg={validateInputLength('name', milestone.name)}
                                />
                            ) : (
                                <CustomizedTooltip
                                    sx={{
                                        '&.MuiTooltip-popper[data-popper-placement*="bottom"] .MuiTooltip-tooltip':
                                            { marginTop: '8px' },
                                    }}
                                    disableTouchListener
                                    onClose={() => handleTooltipClose('name')}
                                    open={openMilNameTooltip === milestone.id}
                                    title={milestone.name}
                                    TransitionComponent={Zoom}
                                    TransitionProps={{ timeout: 240 }}
                                    placement="bottom-start"
                                >
                                    <span
                                        className={cn(
                                            styles['table-row'],
                                            styles['table-row__name']
                                        )}
                                        onClick={() => handleTooltipOpen('name', milestone.id)}
                                        onMouseEnter={() => handleTooltipOpen('name', milestone.id)}
                                        onMouseLeave={() => handleTooltipClose('name')}
                                    >
                                        {milestone.name}
                                    </span>
                                </CustomizedTooltip>
                            )}
                        </LTItem>
                        <LTItem style={{ ...cellLeft, paddingRight: '8px', overflow: 'visible' }}>
                            {milestoneIdInEditMode === milestone.id ? (
                                <StyledTableInputWithError
                                    value={milestone.description}
                                    placeholder={t('milestoneExplanation', { lng: language })}
                                    onChange={(event) =>
                                        changeMilestone(
                                            'description',
                                            event.target.value,
                                            milestone.id
                                        )
                                    }
                                    errorMsg={validateInputLength(
                                        'explanation',
                                        milestone.description
                                    )}
                                />
                            ) : (
                                <CustomizedTooltip
                                    sx={{
                                        '&.MuiTooltip-popper[data-popper-placement*="bottom"] .MuiTooltip-tooltip':
                                            { marginTop: '8px' },
                                    }}
                                    disableTouchListener
                                    onClose={() => handleTooltipClose('explanation')}
                                    open={openMilExplanationTooltip === milestone.id}
                                    title={milestone.description}
                                    TransitionComponent={Zoom}
                                    TransitionProps={{ timeout: 240 }}
                                    placement="bottom-start"
                                >
                                    <span
                                        className={cn(
                                            styles['table-row'],
                                            styles['table-row__desc']
                                        )}
                                        onClick={() =>
                                            handleTooltipOpen('explanation', milestone.id)
                                        }
                                        onMouseEnter={() =>
                                            handleTooltipOpen('explanation', milestone.id)
                                        }
                                        onMouseLeave={() => handleTooltipClose('explanation')}
                                    >
                                        {milestone.description}
                                    </span>
                                </CustomizedTooltip>
                            )}
                        </LTItem>
                        <LTItem style={{ ...cellCenter, paddingRight: '8px', overflow: 'visible' }}>
                            {milestoneIdInEditMode === milestone.id ? (
                                <StyledTableInputWithError
                                    value={milestone.price}
                                    placeholder={t('fee', { lng: language })}
                                    padding="7px 10px"
                                    dangerColor={!isAvailableFeeForFirstMilestone() && idx === 0}
                                    onChange={(event) =>
                                        changeMilestone('price', event.target.value, milestone.id)
                                    }
                                    errorMsg={milestone.price.length === 0 ? 'Required' : undefined}
                                />
                            ) : (
                                <span className={getFeeStyles(idx)}>
                                    {milestone.price.toString().includes('.')
                                        ? `${milestone.price}0`
                                        : `${milestone.price}`}
                                </span>
                            )}
                        </LTItem>
                        {editMode && (
                            <LTItem style={cellCenter}>
                                <IconBtn
                                    variant="outlined"
                                    disabled={hasErrors}
                                    onClick={() => toggleRowInEditMode(milestone.id)}
                                >
                                    {milestoneIdInEditMode === milestone.id ? (
                                        <CheckIcon
                                            style={{
                                                transform: 'scale(1.3)',
                                                cursor: 'pointer',
                                                fontSize: 'unset',
                                            }}
                                            fill={hasErrors ? '#C0C0C0' : '#282828'}
                                        />
                                    ) : (
                                        <EditIcon
                                            style={{
                                                transform: 'scale(1.3)',
                                                width: '19',
                                                height: '18px',
                                                cursor: 'pointer',
                                                fontSize: 'unset',
                                            }}
                                            fill={hasErrors ? '#C0C0C0' : '#282828'}
                                        />
                                    )}
                                </IconBtn>
                                <IconBtn
                                    ml="6px"
                                    variant="outlined"
                                    onClick={() => removeMilestone(milestone.id)}
                                >
                                    <BasketIcon fill="#282828" />
                                </IconBtn>
                            </LTItem>
                        )}

                        {/*SMALL SCREEN VERSION*/}
                        <div style={mobile ? { height: '35px' } : { display: 'none' }}>
                            <LTMobileItem name={t('name', { lng: language })}>
                                {milestoneIdInEditMode === milestone.id ? (
                                    <StyledTableInputWithError
                                        value={milestone.name}
                                        padding="4px 12px"
                                        onChange={(event) =>
                                            changeMilestone(
                                                'name',
                                                event.target.value,
                                                milestone.id
                                            )
                                        }
                                        placeholder={getNamePlaceholder(idx)}
                                        errorMsg={validateInputLength('name', milestone.name)}
                                    />
                                ) : (
                                    milestone.name
                                )}
                            </LTMobileItem>
                        </div>

                        <div style={mobile ? { height: '35px' } : { display: 'none' }}>
                            <LTMobileItem name={t('explanation', { lng: language })}>
                                {milestoneIdInEditMode === milestone.id ? (
                                    <StyledTableInputWithError
                                        value={milestone.description}
                                        placeholder={t('milestoneExplanation', { lng: language })}
                                        padding="4px 12px"
                                        onChange={(event) =>
                                            changeMilestone(
                                                'description',
                                                event.target.value,
                                                milestone.id
                                            )
                                        }
                                        errorMsg={validateInputLength(
                                            'explanation',
                                            milestone.description
                                        )}
                                    />
                                ) : (
                                    milestone.description
                                )}
                            </LTMobileItem>
                        </div>

                        <div style={mobile ? { height: '35px' } : { display: 'none' }}>
                            <LTMobileItem name={t('fee', { lng: language })}>
                                {milestoneIdInEditMode === milestone.id ? (
                                    <StyledTableInputWithError
                                        value={milestone.price}
                                        placeholder={t('fee', { lng: language })}
                                        padding="4px 12px"
                                        dangerColor={
                                            !isAvailableFeeForFirstMilestone() && idx === 0
                                        }
                                        onChange={(event) =>
                                            changeMilestone(
                                                'price',
                                                event.target.value,
                                                milestone.id
                                            )
                                        }
                                        errorMsg={
                                            milestone.price.length === 0 ? 'Required' : undefined
                                        }
                                    />
                                ) : (
                                    <span className={getFeeStyles(idx)}>
                                        {milestone.price.toString().includes('.')
                                            ? `${milestone.price}0`
                                            : `${milestone.price}`}
                                    </span>
                                )}
                            </LTMobileItem>
                        </div>

                        {editMode && (
                            <div style={mobile ? { height: '35px' } : { display: 'none' }}>
                                <LTMobileItem
                                    style={
                                        milestoneIdInEditMode === milestone.id
                                            ? { marginRight: '5px' }
                                            : {}
                                    }
                                    name={t('actions', { lng: language })}
                                >
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <IconBtn
                                            style={{ padding: '0px', marginRight: '6px' }}
                                            disabled={hasErrors}
                                            onClick={() => toggleRowInEditMode(milestone.id)}
                                        >
                                            {milestoneIdInEditMode === milestone.id ? (
                                                <CheckIcon
                                                    style={{
                                                        transform: 'scale(1.3)',
                                                        cursor: 'pointer',
                                                        fontSize: 'unset',
                                                    }}
                                                    fill={hasErrors ? '#C0C0C0' : '#282828'}
                                                />
                                            ) : (
                                                <EditIcon
                                                    style={{
                                                        transform: 'scale(1.3)',
                                                        width: '19',
                                                        height: '18px',
                                                        cursor: 'pointer',
                                                        fontSize: 'unset',
                                                    }}
                                                    fill={hasErrors ? '#C0C0C0' : '#282828'}
                                                />
                                            )}
                                        </IconBtn>
                                        <IconBtn
                                            style={{ padding: '0px' }}
                                            onClick={() => removeMilestone(milestone.id)}
                                        >
                                            {/* <BasketIcon fill="#282828" /> */}
                                            <SvgIcon
                                                style={{
                                                    width: '15',
                                                    height: '16.7px',
                                                    cursor: 'pointer',
                                                    fontSize: 'unset',
                                                }}
                                                viewBox="3 5 18 16.7"
                                                component={BiTrash}
                                            />
                                        </IconBtn>
                                    </div>
                                </LTMobileItem>
                            </div>
                        )}
                    </LTRow>
                ))}

                {/* CREATE NEW MILESTONE */}
                {preparedMilestones.length < 6 && editMode && (
                    <>
                        {/*FULL SCREEN VERSION*/}
                        <LTRow innerStyle={mobile ? undefined : oldGridStyle}>
                            <LTItem
                                style={{ ...cellLeft, paddingRight: '8px', overflow: 'visible' }}
                            >
                                <StyledTableInputWithError
                                    value={name}
                                    onChange={(event) => setName(event.target.value)}
                                    placeholder={getNamePlaceholder(preparedMilestones.length)}
                                    errorMsg={validateMaxLength('name', name)}
                                />
                            </LTItem>
                            <LTItem
                                style={{ ...cellLeft, paddingRight: '8px', overflow: 'visible' }}
                            >
                                <StyledTableInputWithError
                                    value={explanation}
                                    onChange={(event) => setExplanation(event.target.value)}
                                    placeholder={t('milestoneExplanation', { lng: language })}
                                    errorMsg={validateMaxLength('explanation', explanation)}
                                />
                            </LTItem>
                            <LTItem style={{ ...cellCenter, paddingRight: '8px' }}>
                                <StyledTableInput
                                    value={price}
                                    placeholder={t('fee', { lng: language })}
                                    padding="7px 10px"
                                    onChange={(event) => handlePriceInput(event.target.value)}
                                />
                            </LTItem>
                            <LTItem style={cellCenter}>
                                <IconBtn
                                    variant="outlined"
                                    onClick={() => addMilestone()}
                                    disabled={!isCreateBtnEnable}
                                >
                                    {!isCreating && (
                                        <PlusIcon
                                            fill={isCreateBtnEnable ? '#282828' : '#C0C0C0'}
                                        />
                                    )}
                                    {isCreating && (
                                        <div className={styles.loader}>
                                            <CircularProgress size={14} color="primary" />
                                        </div>
                                    )}
                                </IconBtn>
                            </LTItem>

                            {/*SMALL SCREEN VERSION*/}
                            <div className={styles['input-wrapper']}>
                                <div className={styles['input-wrapper_mobile']}>
                                    <StyledTableInputWithError
                                        value={name}
                                        onChange={(event) => setName(event.target.value)}
                                        placeholder={getNamePlaceholder(preparedMilestones.length)}
                                        errorMsg={validateMaxLength('name', name)}
                                    />
                                </div>

                                <div className={styles['input-wrapper_mobile']}>
                                    <StyledTableInputWithError
                                        value={explanation}
                                        onChange={(event) => setExplanation(event.target.value)}
                                        placeholder={t('milestoneExplanation', { lng: language })}
                                        errorMsg={validateMaxLength('explanation', explanation)}
                                    />
                                </div>

                                <div className={styles['input-wrapper__action']}>
                                    <StyledTableInput
                                        value={price}
                                        placeholder={t('fee', { lng: language })}
                                        padding="7px 14px"
                                        width="80px"
                                        onChange={(event) => handlePriceInput(event.target.value)}
                                    />
                                    <IconBtn
                                        variant="outlined"
                                        onClick={() => addMilestone()}
                                        disabled={!isCreateBtnEnable}
                                    >
                                        {!isCreating && (
                                            <PlusIcon
                                                fill={isCreateBtnEnable ? '#282828' : '#C0C0C0'}
                                            />
                                        )}
                                        {isCreating && (
                                            <div className={styles.loader}>
                                                <CircularProgress size={14} color="primary" />
                                            </div>
                                        )}
                                    </IconBtn>
                                </div>
                            </div>
                        </LTRow>
                    </>
                )}
            </ListTable>
            {!isAvailableFeeForFirstMilestone() && (
                <span className={styles.msg}>{t('theFirstMilestoneMust', { lng: language })}</span>
            )}
        </div>
    );
};

export default OldMilestonesAndFees;
